import { apiFetch } from '../lib/api';
import { track } from '../lib/track';
import React, { useEffect, useRef, useState } from 'react';
import { beginCell } from '@ton/core';
import { formatTonExact } from '../lib/ton-format';
// CF-BRAND-LION-DIAMOND-V1: bundled diamond mark replaces platform 💎 emoji.
import cfDiamondMark from '../assets/brand/cf-diamond.svg';
import { deriveDepositAmount } from '../lib/deposit-amount';
import { useT, useLang } from '../i18n';
import { isDragonTiger, isGerman, isBrazil } from '../lib/app-variant';
// CF-BONUS Ticket B: the first-deposit offer teaser. Read-only; the modal never infers
// eligibility itself — it renders whatever GET /api/v1/bonus said, and nothing at all if that
// read failed (Scorer A1/A5: never advertise an offer we could not confirm).
import { BonusTeaser } from './BonusPanel';
import { NETWORK_UNAVAILABLE, type BonusView } from '../lib/bonus-state';
import { BONUS_AVAILABLE_IN_BUILD } from '../lib/bonus-availability';

// Language-aware deposit preset amounts (TON). The high-roller launch raised the
// max stake to 50 TON, so the deposit presets are correspondingly larger.
// Chinese players see culturally-auspicious 8-based amounts; everyone else gets
// the round-number default ladder. Selection happens INSIDE the component because
// the resolved language is only available via the useLang() hook.
const DEPOSIT_AMOUNTS_EN = [5, 25, 50, 100, 250];
const DEPOSIT_AMOUNTS_ZH = [8, 38, 88, 188, 388];

// Per-deposit custodial reminder shown on the deposit pick screen is rendered
// via t('deposit.custodial_tooltip'). The full custodial trust model lives in
// the Terms of Service (TermsModal / terms-content.ts).

// Balance polling parameters. The deposit-watcher polls Toncenter every few
// seconds and credits the user_balance row as soon as it sees the matched
// memo invoice at the configured finality depth. 5-minute ceiling gives the
// player a "Check again" affordance instead of a stuck spinner.
const BALANCE_POLL_INTERVAL_MS = 5_000;
const BALANCE_POLL_TIMEOUT_MS = 300_000;

interface DepositModalProps {
    apiBase: string;
    tonConnectUI: any;
    // Informational only — the deposit is matched by invoice memo, not by
    // sender address. Kept in props so the prior call site does not need to
    // change at the same time as this rewrite.
    walletAddress?: string;
    onClose: () => void;
    onDeposited: () => void;
    /**
     * CF-BONUS Ticket B: the bonus view, RELAYED from App (the single owner). The modal does
     * not fetch — R2 shipped with the only fetch living here, which is why a bonus issued
     * mid-session stayed invisible until the modal was reopened.
     * Defaults to `unavailable`, and BonusTeaser renders nothing for that, so a caller that
     * has not been updated advertises nothing rather than guessing.
     */
    bonusView?: BonusView;
}

type Step =
    | 'pick'
    | 'creating_invoice'
    | 'sending'
    | 'confirming'
    | 'verification_delayed'
    | 'done'
    | 'error';

export function DepositModal({ apiBase, tonConnectUI, onClose, onDeposited, bonusView = NETWORK_UNAVAILABLE }: DepositModalProps) {
    const t = useT();
    const lang = useLang();
    // Language-aware preset amounts: Chinese players get the 8-based ladder,
    // everyone else the default ladder. Resolved per-render from the session lang
    // (language_code is fixed per session, so this is effectively constant).
    const depositAmounts = lang === 'zh' ? DEPOSIT_AMOUNTS_ZH : DEPOSIT_AMOUNTS_EN;
    // Amount selection: a quick chip OR a free-form custom entry, mutually exclusive.
    // Default chip = the second preset (a sensible mid-tier starting pick that
    // exists in BOTH ladders: 25 EN / 38 ZH).
    const [chip, setChip] = useState<number | null>(depositAmounts[1]);
    const [custom, setCustom] = useState<string>('');
    const [step, setStep] = useState<Step>('pick');
    const [errorMsg, setErrorMsg] = useState('');
    const [confirmSec, setConfirmSec] = useState(0);
    const [invoiceId, setInvoiceId] = useState<string | null>(null);

    const counterTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Snapshot of the initial balance + remembered watch parameters so the
    // "Check again" button can resume the credit search without re-issuing the
    // invoice or re-sending TON.
    const watchRef = useRef<{ startedAt: number; initialNano: bigint } | null>(null);
    // The EXACT amount committed when the deposit was initiated, frozen in a ref so
    // the confirming/done screens + analytics can never drift from what was actually
    // sent on-chain (a ref, not state, so the poll/track closures read it synchronously).
    const committedNanoRef = useRef<bigint | null>(null);
    const [committedNano, setCommittedNano] = useState<bigint | null>(null);

    useEffect(() => () => {
        if (counterTimerRef.current) clearInterval(counterTimerRef.current);
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    }, []);

    // Derived, fail-loud amount for the pick screen.
    const derived = deriveDepositAmount(chip, custom);
    const amountNano = derived.kind === 'ok' ? derived.nano : null;
    const amountError = derived.kind === 'error' ? derived.message : null;

    const stopTimers = () => {
        if (counterTimerRef.current) {
            clearInterval(counterTimerRef.current);
            counterTimerRef.current = null;
        }
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    const fetchBalanceNano = async (): Promise<bigint> => {
        const r = await apiFetch(`${apiBase}/api/v1/balance`);
        if (!r.ok) throw new Error(`balance_http_${r.status}`);
        const data = await r.json();
        return BigInt(data.balance_nano);
    };

    // Analytics-only TON number derived from the exact committed nano. Never used
    // for any economic decision — purely a Plausible prop.
    const committedTonForTrack = (): number => {
        const n = committedNanoRef.current;
        return n === null ? 0 : Number(formatTonExact(n));
    };

    const pollForCredit = (startedAt: number, initialNano: bigint) => {
        if (Date.now() - startedAt > BALANCE_POLL_TIMEOUT_MS) {
            stopTimers();
            setStep('verification_delayed');
            return;
        }
        apiFetch(`${apiBase}/api/v1/balance`)
            .then(async (r) => {
                if (!r.ok) throw new Error(`balance_http_${r.status}`);
                const data = await r.json();
                const currentNano = BigInt(data.balance_nano);
                if (currentNano > initialNano) {
                    stopTimers();
                    setStep('done');
                    track.depositSuccess(committedTonForTrack(), 'credited');
                    setTimeout(() => onDeposited(), 1500);
                    return;
                }
                pollTimerRef.current = setTimeout(
                    () => pollForCredit(startedAt, initialNano),
                    BALANCE_POLL_INTERVAL_MS,
                );
            })
            .catch((err) => {
                // Transient network blip — keep polling. The deposit-watcher is
                // authoritative; we'll see the credit eventually or hit the
                // 5-minute ceiling and offer "Check again".
                console.error('🚨 balance_poll_failed', err);
                pollTimerRef.current = setTimeout(
                    () => pollForCredit(startedAt, initialNano),
                    BALANCE_POLL_INTERVAL_MS,
                );
            });
    };

    const startConfirming = (initialNano: bigint) => {
        const startedAt = Date.now();
        watchRef.current = { startedAt, initialNano };
        setStep('confirming');
        setConfirmSec(0);
        stopTimers();
        counterTimerRef.current = setInterval(() => setConfirmSec((s) => s + 1), 1000);
        pollTimerRef.current = setTimeout(
            () => pollForCredit(startedAt, initialNano),
            BALANCE_POLL_INTERVAL_MS,
        );
    };

    const checkAgain = () => {
        const w = watchRef.current;
        if (!w) {
            // No prior watch state — fall back to a fresh balance fetch.
            fetchBalanceNano()
                .then((current) => {
                    watchRef.current = { startedAt: Date.now(), initialNano: current };
                    startConfirming(current);
                })
                .catch(() => setStep('verification_delayed'));
            return;
        }
        startConfirming(w.initialNano);
    };

    const handleDeposit = async () => {
        // Guard: only proceed with a valid, in-range amount. The button is disabled
        // when this is null, but re-check here so a malformed amount can never be sent.
        if (amountNano === null) return;
        const depositNano = amountNano;
        committedNanoRef.current = depositNano;
        setCommittedNano(depositNano);

        track.depositInitiated(Number(formatTonExact(depositNano)));
        let walletSent = false;

        try {
            // 1. Snapshot pre-deposit balance.
            setStep('creating_invoice');
            const initialNano = await fetchBalanceNano();

            // 2. Mint a deposit invoice; backend returns the hot-wallet address
            //    and the invoice_id the deposit-watcher will look for as memo.
            const invoiceResp = await apiFetch(`${apiBase}/api/v1/deposits/invoice`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!invoiceResp.ok) {
                const data = await invoiceResp.json().catch(() => ({} as any));
                if (data.error === 'maintenance_mode') {
                    setErrorMsg(t('deposit.error.maintenance'));
                } else if (data.error === 'hot_wallet_unconfigured') {
                    setErrorMsg(t('deposit.error.unconfigured'));
                } else {
                    setErrorMsg(t('deposit.error.invoice_failed'));
                }
                setStep('error');
                track.depositFail(committedTonForTrack(), `invoice_http_${invoiceResp.status}`);
                return;
            }
            const invoice = await invoiceResp.json() as { invoice_id?: string; hot_wallet_address?: string };
            const { invoice_id, hot_wallet_address } = invoice;
            if (typeof invoice_id !== 'string' || typeof hot_wallet_address !== 'string') {
                setErrorMsg(t('deposit.error.invoice_invalid'));
                setStep('error');
                track.depositFail(committedTonForTrack(), 'invoice_invalid_shape');
                return;
            }
            setInvoiceId(invoice_id);

            // 3. Build the TON text-comment memo body (op=0 + storeStringTail).
            //    The deposit-watcher matches incoming TXs to pending invoices by
            //    parsing this memo. base64 BoC is the TonConnect payload form.
            const memoPayload = beginCell()
                .storeUint(0, 32)
                .storeStringTail(invoice_id)
                .endCell()
                .toBoc()
                .toString('base64');

            // 4. Send TON via TonConnect (single approval). depositNano is the exact
            //    string-parsed value (no binary-float multiply), so the on-chain amount
            //    is exactly what the player selected.
            setStep('sending');
            await tonConnectUI.sendTransaction({
                validUntil: Math.floor(Date.now() / 1000) + 300,
                messages: [
                    {
                        address: hot_wallet_address,
                        amount: depositNano.toString(),
                        payload: memoPayload,
                    },
                ],
            });
            walletSent = true;

            // 5. Start the balance-poll loop. The watcher is authoritative —
            //    we just observe the user_balance row from the same source the
            //    rest of the app trusts.
            startConfirming(initialNano);
        } catch (err: any) {
            if (err?.message?.includes('Cancelled') || err?.message?.includes('rejected')) {
                setStep('pick');
                track.depositFail(committedTonForTrack(), 'wallet_cancel');
                return;
            }
            if (walletSent) {
                // TON is in flight on-chain — never tell the user to resend.
                setStep('verification_delayed');
                track.depositFail(committedTonForTrack(), 'post_wallet_failure');
            } else {
                setErrorMsg(t('deposit.error.tx_failed_pre_wallet'));
                setStep('error');
                track.depositFail(committedTonForTrack(), 'pre_wallet_failure');
            }
        }
    };

    const dismissable = step === 'pick' || step === 'error'
        || step === 'verification_delayed' || step === 'done';

    // Exact display strings for the confirming/done screens (frozen committed amount).
    const committedTonDisplay = committedNano !== null ? formatTonExact(committedNano) : '';

    return (
        <div className="fixed inset-0 z-[100] flex items-end justify-center"
             style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
             onClick={dismissable ? onClose : undefined}>
            <div className="w-full max-w-lg rounded-t-3xl"
                 style={{ background: 'var(--bg-card)' }}
                 onClick={e => e.stopPropagation()}>

                {/* Handle bar */}
                <div className="flex justify-center pt-3 pb-2">
                    <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-subtle)' }} />
                </div>

                <div className="px-6 pb-8">
                    {step === 'pick' && (
                        <>
                            <div className="text-center mb-6">
                                <img src={cfDiamondMark} alt="" className="w-11 h-11 mx-auto" />
                                <h2 className="text-xl font-black mt-2">{t('deposit.title')}</h2>
                                <p className="text-[12px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    {t('deposit.subtitle')}
                                </p>
                            </div>

                            {/* Gated inside BonusTeaser: renders ONLY for a server-confirmed
                                `none` + offer_available. Carries the transaction-time wording;
                                displaying it reserves nothing. */}
                            <div className="mb-4">
                                <BonusTeaser view={bonusView} termsHref="/bonus-terms" />
                            </div>

                            {/* Free-form custom amount. Typing here clears the chip selection;
                                the parser (parseTonToNano) is the validation gate.
                                Rendered ABOVE the preset chips so the manual entry is the
                                primary affordance for the high-roller launch. */}
                            <div className="flex items-center gap-2 rounded-xl px-4 mb-2"
                                 style={{
                                     background: 'var(--bg-elevated)',
                                     border: `1.5px solid ${
                                         amountError ? 'rgba(239, 68, 68, 0.45)'
                                         : custom.trim() !== '' ? 'rgba(244, 213, 111, 0.3)'
                                         : 'var(--border-subtle)'}`,
                                 }}>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={custom}
                                    onChange={e => { setCustom(e.target.value); setChip(null); }}
                                    placeholder={t('deposit.custom_amount_placeholder')}
                                    aria-label={t('deposit.custom_amount_aria')}
                                    className="flex-1 bg-transparent py-3 text-[15px] font-bold outline-none"
                                    style={{ color: 'var(--text-primary)' }}
                                />
                                <span className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{t('common.ton_unit')}</span>
                            </div>

                            {/* CF-DRAGONTIGER-THEME-V1 Phase B (D5, Scorer #3):
                                neutral "快捷金额" (quick amounts) label above the
                                deposit preset chips. Variant-gated. MUST NOT imply
                                the preset values improve odds (never 幸运数字). The
                                preset VALUES (8/38/88/188/388) are the existing
                                zh ladder — unchanged here (presentation only). */}
                            {(isDragonTiger || isGerman || isBrazil) && (
                                <p className="text-[11px] font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                                    {t('deposit.quick_amounts_label')}
                                </p>
                            )}
                            <div className="grid grid-cols-5 gap-2 mb-3">
                                {depositAmounts.map(a => {
                                    const selected = chip === a && custom.trim() === '';
                                    return (
                                        <button
                                            key={a}
                                            onClick={() => { setChip(a); setCustom(''); }}
                                            className="py-3 rounded-xl text-center font-bold transition-all active:scale-95"
                                            style={{
                                                background: selected ? 'rgba(244, 213, 111, 0.1)' : 'var(--bg-elevated)',
                                                border: `1.5px solid ${selected ? 'rgba(244, 213, 111, 0.3)' : 'var(--border-subtle)'}`,
                                                color: selected ? 'var(--gold)' : 'var(--text-secondary)',
                                                fontSize: '15px',
                                            }}
                                        >
                                            {a}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="glass rounded-xl p-3 mb-5">
                                {amountError ? (
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('deposit.amount_label')}</span>
                                        <span className="text-sm font-bold" style={{ color: 'var(--red)' }}>
                                            {amountError}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('deposit.summary_deposit_label')}</span>
                                        <span className="text-sm font-bold">
                                            {amountNano !== null ? formatTonExact(amountNano) : '—'} {t('common.ton_unit')}
                                        </span>
                                    </div>
                                )}
                                <p className="text-[10px] leading-snug mt-3" style={{ color: 'var(--text-muted)' }}>
                                    {t('deposit.network_fee_note')}
                                </p>
                            </div>

                            <button
                                onClick={handleDeposit}
                                disabled={amountNano === null}
                                className="btn-primary w-full py-4 rounded-2xl text-[15px] font-black tracking-wide"
                                style={amountNano === null ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                            >
                                {amountNano !== null ? t('deposit.confirm_btn', { amount: formatTonExact(amountNano) }) : t('deposit.confirm_btn_empty')}
                            </button>

                            <p
                                className="text-center text-[10px] mt-3"
                                style={{ color: 'var(--text-muted)' }}
                                title={isDragonTiger || isGerman || isBrazil ? undefined : 'See full trust model in Terms of Service'}
                            >
                                {t('deposit.custodial_tooltip')}
                            </p>
                        </>
                    )}

                    {step === 'creating_invoice' && (
                        <div className="text-center py-8">
                            <span className="inline-block w-8 h-8 border-3 border-white/20 border-t-white/60 rounded-full animate-spin mb-4"
                                  style={{ borderWidth: '3px' }} />
                            <h3 className="text-lg font-bold">{t('deposit.preparing_title')}</h3>
                            <p className="text-[12px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {t('deposit.preparing_sub')}
                            </p>
                        </div>
                    )}

                    {step === 'sending' && (
                        <div className="text-center py-8">
                            <span className="inline-block w-8 h-8 border-3 border-white/20 border-t-white/60 rounded-full animate-spin mb-4"
                                  style={{ borderWidth: '3px' }} />
                            <h3 className="text-lg font-bold">{t('deposit.approve_title')}</h3>
                            <p className="text-[12px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {t('deposit.approve_sub')}
                            </p>
                        </div>
                    )}

                    {step === 'confirming' && (
                        <div className="text-center py-8">
                            <span className="inline-block w-8 h-8 border-3 border-white/20 border-t-white/60 rounded-full animate-spin mb-4"
                                  style={{ borderWidth: '3px' }} />
                            <h3 className="text-lg font-bold">{t('deposit.confirming_title')}</h3>

                            <div className="w-48 h-1.5 rounded-full mx-auto mt-4 mb-3" style={{ background: 'var(--bg-elevated)' }}>
                                <div className="h-full rounded-full transition-all duration-1000"
                                     style={{
                                         background: 'var(--gold)',
                                         width: `${Math.min((confirmSec / 60) * 100, 95)}%`,
                                         opacity: 0.7,
                                     }} />
                            </div>

                            <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                {confirmSec < 15
                                    ? t('deposit.confirming_wait_early')
                                    : confirmSec < 60
                                    ? t('deposit.confirming_wait_mid')
                                    : t('deposit.confirming_wait_late')}
                            </p>
                            <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>
                                {t('deposit.confirming_safe_note')}
                            </p>
                        </div>
                    )}

                    {step === 'done' && (
                        <div className="text-center py-8">
                            <span className="text-5xl">✅</span>
                            <h3 className="text-lg font-black mt-3" style={{ color: 'var(--emerald)' }}>
                                {t('deposit.done_title')}
                            </h3>
                            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {t('deposit.done_credited', { amount: committedTonDisplay })}
                            </p>
                        </div>
                    )}

                    {step === 'verification_delayed' && (
                        <div className="text-center py-8">
                            <span className="text-5xl">⏳</span>
                            <h3 className="text-lg font-black mt-3">{t('deposit.delayed_title')}</h3>
                            <p className="text-[13px] mt-2 px-4 leading-snug" style={{ color: 'var(--text-secondary)' }}>
                                {t('deposit.delayed_body')}
                            </p>
                            {invoiceId && (
                                <p className="text-[10px] mt-3 px-4 font-mono break-all" style={{ color: 'var(--text-muted)' }}>
                                    {t('deposit.delayed_invoice', { invoiceId })}
                                </p>
                            )}
                            <div className="flex gap-2 justify-center mt-5">
                                <button
                                    onClick={checkAgain}
                                    className="btn-primary px-6 py-2.5 rounded-xl text-sm font-bold"
                                >
                                    {t('deposit.check_again_btn')}
                                </button>
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2.5 rounded-xl text-sm font-bold"
                                    style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                                >
                                    {t('common.close_btn')}
                                </button>
                            </div>
                            <p className="text-[10px] mt-4 px-4 leading-snug" style={{ color: 'var(--text-muted)' }}>
                                {t('deposit.delayed_no_resend')}
                            </p>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="text-center py-8">
                            <span className="text-5xl">⚠️</span>
                            <h3 className="text-[15px] font-bold mt-3 px-4 leading-snug">{errorMsg}</h3>
                            <button
                                onClick={() => setStep('pick')}
                                className="btn-primary px-6 py-2.5 rounded-xl text-sm font-bold mt-4"
                            >
                                {t('common.retry_btn')}
                            </button>
                            <p className="text-[9px] mt-3 px-6" style={{ color: 'var(--text-muted)' }}>
                                {t('deposit.error_funds_safe_note')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
